// Standalone widget entry. Exposes:
//
//   window.PebllaLogin = {
//     open(props):       open the login modal/drawer; returns a Promise that
//                        resolves with `{ ok: true, member, sessionId }` on
//                        successful login and rejects on cancel/failure.
//
//     getMember():       synchronously read the cached member info (firstName,
//                        lastName, email, phone, etc.) — works on subsequent
//                        page loads as long as the user's session cookie is
//                        still valid. Returns `null` if not logged in.
//
//     getSessionId():    return the active session id from cookie, or null.
//
//     isLoggedIn():      boolean shortcut.
//
//     logout():          clear the session cookie + cached member info
//                        locally. Does NOT hit any logout API (none exists).
//
//   }
//
// Bootstrapping (must run BEFORE calling any method):
//
//   window.__WEB_SITE_APP_CONFIG = {
//     appConfig: { orgId, channel: 'Web', stylePath, isPebllaDomain },
//   };

import login, { type LoginProps } from "@/widgets/login";
import { commonStore } from "@/stores/quick-order";
import { storageHelper } from "@/utils";
import Cookies from "js-cookie";

const SESSION_COOKIE = "__session_id";
const MEMBER_KEY = "__member_info";

interface PebllaMember {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  memberId?: string | number;
  [key: string]: unknown;
}

declare global {
  interface Window {
    PebllaLogin?: {
      open: (props?: LoginProps) => Promise<unknown> | undefined;
      getMember: () => PebllaMember | null;
      getSessionId: () => string | null;
      isLoggedIn: () => boolean;
      logout: () => void;
      readonly version: string;
    };
    __WEB_SITE_APP_CONFIG?: {
      appConfig?: Record<string, unknown>;
      antdToken?: Record<string, unknown>;
      [key: string]: unknown;
    };
  }
}

const VERSION = "2.0.0";

function open(props: LoginProps = {}) {
  if (typeof window === "undefined") return;
  return login(props);
}

function getMember(): PebllaMember | null {
  if (typeof window === "undefined") return null;
  // Prefer the in-memory store (freshest), fall back to localStorage.
  const inMemory = (commonStore as any).memberInfo;
  if (inMemory && Object.keys(inMemory).length > 0) return inMemory;
  try {
    return storageHelper.getItem(MEMBER_KEY) ?? null;
  } catch {
    return null;
  }
}

function getSessionId(): string | null {
  if (typeof window === "undefined") return null;
  return Cookies.get(SESSION_COOKIE) ?? null;
}

function isLoggedIn(): boolean {
  return Boolean(getSessionId() && getMember());
}

function logout(): void {
  if (typeof window === "undefined") return;
  Cookies.remove(SESSION_COOKIE, { path: "/" });
  Cookies.remove(SESSION_COOKIE);
  try {
    storageHelper.delete(MEMBER_KEY);
  } catch {
    /* noop */
  }
  (commonStore as any).memberInfo = {};
  (commonStore as any).isLogin = false;
}

if (typeof window !== "undefined" && !window.PebllaLogin) {
  window.PebllaLogin = Object.freeze({
    open,
    getMember,
    getSessionId,
    isLoggedIn,
    logout,
    version: VERSION,
  });
}

export {};
